import React, { useState } from 'react';
import { Button, Divider, Grid, Typography } from '@mui/material';
import DataTypeGlobalSelector from '../common/DataTypeGlobalSelector';
import FieldLabel from './FieldLabel';
import FieldNew from './FieldNew';
import FieldTags from './FieldTags';
import FieldsOrder from './FieldsOrder';
import FieldsSettings from './FieldsSettings/FieldsSettings';
import FieldsTopLabel from './FieldsSettings/FieldsTopLabel';
import ImportFields from './ImportFields';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import { DataTypesAPIResponse, TrainingSetDataConfig } from './FieldsSettings/FieldsSettingsTypes';
import { IField } from '../../types/my-types';
import { styled } from '@mui/system';
import { getFieldDataTypeAndConfig } from './utils/utilsFields';

export interface FieldsProps {
  dataTypesResponse: DataTypesAPIResponse[];
  fields: Array<IField>;
  limit?: number;
  offSet?: number;
  page: number;
  total: number;
  trainingSetDataTypeConfig: TrainingSetDataConfig | null | undefined;
  trainingSetDataTypeIsGlobal?: boolean;
  trainingSetId: string;
  trainingSetLabel: string;
  previousPage: () => void;
  nextPage: () => void;
}

const GridFieldTags = styled(Grid)({
  '& .MuiTextField-root': {
    width: 'calc(100% - 16px)',
    maxWidth: '280px',
  },
});

const DataTypesPadding = '1em';

const Fields: React.FC<FieldsProps> = ({
  dataTypesResponse,
  fields,
  page,
  total,
  trainingSetDataTypeConfig,
  trainingSetDataTypeIsGlobal,
  trainingSetId,
  trainingSetLabel,
  previousPage,
  nextPage,
}) => {
  const [clearFieldsFormats, setClearFieldsFormats] = useState(true);
  const [isReorder, setIsReorder] = React.useState(false);
  const [editTags, setEditTags] = React.useState(false);
  let fieldLabels = fields.map((f: IField) => f.label);

  React.useEffect(() => {
    if (fields.length <= 0) {
      previousPage();
    }
  }, [fields, previousPage]);

  function Pagination() {
    if (total <= 1) {
      return null;
    }

    return (
      <div
        style={{
          width: 400,
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <Button variant="outlined" onClick={() => previousPage()}>
          <KeyboardArrowLeft />
          Previous
        </Button>
        <Typography variant="subtitle1">
          {page + 1}
          of
          {total}
        </Typography>
        <Button variant="outlined" onClick={() => nextPage()}>
          Next
          <KeyboardArrowRight />
        </Button>
      </div>
    );
  }

  return (
    <div id="field-page">
      <div>
        <Grid container={true} spacing={1}>
          <Grid item={true} xs={12}>
            <div style={{ display: 'flex', flexWrap: 'wrap' }}>
              {isReorder ? (
                <div style={{ display: 'inline-block', paddingLeft: '2em' }}>
                  <Button
                    variant="outlined"
                    onClick={() => setIsReorder(false)}
                    style={{ minWidth: '120px' }}
                  >
                    Edit Fields
                  </Button>
                </div>
              ) : (
                <>
                  <div style={{ display: 'inline-block', paddingLeft: '2em' }}>
                    <Button
                      variant="outlined"
                      onClick={() => setIsReorder(true)}
                      style={{ minWidth: '150px' }}
                    >
                      Reorder Fields
                    </Button>
                  </div>
                  <div style={{ display: 'inline-block', paddingLeft: '2em' }}>
                    <ImportFields iconType="button" />
                  </div>
                </>
              )}

              <div style={{ display: 'inline-block', paddingLeft: '2em' }}>
                {dataTypesResponse && (
                  <DataTypeGlobalSelector
                    dataTypesResponse={dataTypesResponse}
                    setClearFieldsFormats={setClearFieldsFormats}
                    trainingSetDataTypeConfig={trainingSetDataTypeConfig}
                    trainingSetId={trainingSetId}
                  />
                )}

                {!isReorder ? (
                  <Button
                    variant="outlined"
                    onClick={() => setEditTags(!editTags)}
                    data-cy="btn-edit-legacy-tags"
                  >
                    Edit Legacy Tags
                  </Button>
                ) : null}
              </div>

              <div
                style={{
                  display: 'inline-block',
                  paddingLeft: '1em',
                  paddingRight: '2em',
                }}
              >
                {trainingSetDataTypeIsGlobal !== undefined ? (
                  <FieldsTopLabel trainingSetDataTypeIsGlobal={trainingSetDataTypeIsGlobal} />
                ) : null}
              </div>
            </div>
          </Grid>
          <Grid
            container={true}
            spacing={1}
            sx={{
              backgroundColor: 'white',
              margin: 0,
              position: 'sticky',
              top: '-10px',
              width: '400px',
              zIndex: 500,
            }}
          >
            {isReorder ? null : (
              <FieldNew
                fieldLabels={fieldLabels}
                trainingSetId={trainingSetId}
                width={400}
                cypressId="input-new-field"
              />
            )}
          </Grid>
          {isReorder ? (
            <div style={{ marginTop: '2em' }}>
              <FieldsOrder
                trainingSetId={trainingSetId}
                getFieldDataTypeAndConfig={(fieldId) => getFieldDataTypeAndConfig(fields, fieldId)}
              />
            </div>
          ) : (
            <>
              <Grid container={true} spacing={1} style={{ margin: '1em 0' }}>
                <Pagination />
              </Grid>
              <Grid item={true} xs={3} lg={3}>
                <Typography variant="h6">Field Label</Typography>
              </Grid>

              <Grid item={true} xs={2} lg={1} style={{ paddingLeft: DataTypesPadding }}>
                <Typography variant="h6">Data Type</Typography>
              </Grid>
              <Grid item={true} xs={2} lg={1} style={{ paddingLeft: DataTypesPadding }}>
                <Typography variant="h6">Format</Typography>
              </Grid>

              {editTags ? (
                <Grid item={true} xs={2} lg={2}>
                  <Typography variant="h6">Tags</Typography>
                </Grid>
              ) : null}
              <Grid item={true} xs={editTags ? 3 : 5} lg={editTags ? 5 : 7}>
                <Typography variant="h6">Glynt Tags</Typography>
              </Grid>
              <Divider />
              {fields.map((field: IField, index: number) => {
                return (
                  <React.Fragment key={`field-${field.label}`}>
                    <Grid item={true} xs={3} lg={3}>
                      <FieldLabel
                        fieldId={field.id}
                        fieldLabel={field.label}
                        getFieldDataTypeAndConfig={(fieldId) =>
                          getFieldDataTypeAndConfig(fields, fieldId)
                        }
                        trainingSetId={trainingSetId}
                        page={page}
                        field={field}
                      />
                    </Grid>

                    <FieldsSettings
                      clearFieldsFormats={clearFieldsFormats}
                      dataTypesPadding={DataTypesPadding}
                      dataTypesResponse={dataTypesResponse || []}
                      field={field}
                      setClearFieldsFormats={setClearFieldsFormats}
                      trainingSetDataTypeConfig={trainingSetDataTypeConfig}
                    />

                    {editTags ? (
                      <GridFieldTags item={true} xs={2} lg={2}>
                        <FieldTags
                          fieldTags={field.tags}
                          fieldId={field.id}
                          fieldLabel={field.label}
                          getFieldDataTypeAndConfig={(fieldId) =>
                            getFieldDataTypeAndConfig(fields, fieldId)
                          }
                          isGlyntTag={false}
                          trainingSetId={trainingSetId}
                        />
                      </GridFieldTags>
                    ) : null}

                    <Grid item={true} xs={editTags ? 3 : 5} lg={editTags ? 5 : 7}>
                      <FieldTags
                        fieldTags={field.glynt_tags}
                        fieldId={field.id}
                        fieldLabel={field.label}
                        getFieldDataTypeAndConfig={(fieldId) =>
                          getFieldDataTypeAndConfig(fields, fieldId)
                        }
                        isGlyntTag={true}
                        trainingSetId={trainingSetId}
                      />
                    </Grid>
                  </React.Fragment>
                );
              })}
              <Grid container={true} spacing={1} style={{ margin: '1em 0' }}>
                <Pagination />
              </Grid>
            </>
          )}
        </Grid>
      </div>
    </div>
  );
};

export default Fields;
